import React, { useState, useEffect } from 'react';
import { EnhancedCharacter } from '../../utils/ClassComponentRegistry';
import { resolveDomainCardReferences, ResolvedDomainCardWithCharacterData } from '../../utils/contentResolver';
import DomainCard from '../CharacterSheet/DomainCard';
import styles from './DomainCardReferenceSection.module.css';

export interface DomainCardReferenceSectionProps {
  enhancedCharacter: EnhancedCharacter;
}

/**
 * DomainCardReferenceSection - Quick reference for active domain cards (SECONDARY information)
 *
 * Shows:
 * - Active loadout domain cards (max 5)
 * - Card name, domain, level, type
 * - Expandable descriptions for quick reference
 *
 * This is SECONDARY information - domain cards that affect character abilities and can be used in combat.
 * Reuses existing DomainCard component from CharacterSheet.
 */
export const DomainCardReferenceSection: React.FC<DomainCardReferenceSectionProps> = ({
  enhancedCharacter,
}) => {
  const { character } = enhancedCharacter;
  const [loadoutCards, setLoadoutCards] = useState<ResolvedDomainCardWithCharacterData[]>([]);

  // Resolve loadout cards on mount/update
  useEffect(() => {
    if (!character) return;

    const resolveLoadout = async () => {
      const loadoutRefs = character.inventory?.domainCardLoadout || [];

      if (loadoutRefs.length === 0) {
        setLoadoutCards([]);
        return;
      }

      try {
        const resolved = await resolveDomainCardReferences(loadoutRefs);
        // Filter out null results
        const validCards = resolved.filter(
          (card): card is ResolvedDomainCardWithCharacterData => card !== null
        );
        setLoadoutCards(validCards);
      } catch (error) {
        console.error('Failed to resolve domain card loadout:', error);
        setLoadoutCards([]);
      }
    };

    resolveLoadout();
  }, [character?.inventory?.domainCardLoadout]);

  if (!character) {
    return (
      <div className={styles.domainCardReferenceSection}>
        <div className={styles.domainCardReferenceSection__loading}>
          Loading domain cards...
        </div>
      </div>
    );
  }

  return (
    <section
      className={styles.domainCardReferenceSection}
      aria-labelledby="domain-card-reference-heading"
    >
      <h2 id="domain-card-reference-heading" className={styles.domainCardReferenceSection__title}>
        Domain Card Loadout
      </h2>

      <div className={styles.domainCardReferenceSection__content}>
        {loadoutCards.length > 0 ? (
          <div className={styles.domainCardReferenceSection__cardsGrid}>
            {loadoutCards.map((card) => (
              <DomainCard
                key={card.id}
                item={card as any}
                defaultExpanded={false}
              />
            ))}
          </div>
        ) : (
          <div className={styles.domainCardReferenceSection__emptyState}>
            <div className={styles.domainCardReferenceSection__emptyIcon}>📜</div>
            <div className={styles.domainCardReferenceSection__emptyText}>
              No active domain cards
            </div>
            <div className={styles.domainCardReferenceSection__emptyHint}>
              Add domain cards to your loadout in Character Sheet
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default DomainCardReferenceSection;
